import { projectCameraRayWasmSync, unprojectCameraPixelWasmSync } from './readers/tiffWasm';
export const CAMERA_MODEL_COEFFICIENTS = {
    'pinhole-ideal': [],
    'pinhole-opencv': [
        'k1',
        'k2',
        'p1',
        'p2',
        'k3',
        'k4',
        'k5',
        'k6',
        's1',
        's2',
        's3',
        's4',
        'tauX',
        'tauY',
    ],
    'fisheye-equidistant': [],
    'fisheye-opencv': ['k1', 'k2', 'k3', 'k4'],
    'fisheye-kb3': ['k0', 'k1', 'k2', 'k3'],
    fisheye624: ['k0', 'k1', 'k2', 'k3', 'k4', 'k5', 'p0', 'p1', 's0', 's1', 's2', 's3'],
    'e57-pinhole': [],
    'e57-spherical': [],
    'e57-cylindrical': [],
};
/**
 * Coefficient-list lengths the kernel accepts, per model. OpenCV's pinhole is
 * the only one with a choice: its 4, 5, 8, 12 and 14 coefficient layouts are
 * all valid, and a shorter list means the higher terms are zero.
 */
const ACCEPTED_COEFFICIENT_COUNTS = {
    'pinhole-ideal': [0],
    'pinhole-opencv': [4, 5, 8, 12, 14],
    'fisheye-equidistant': [0],
    'fisheye-opencv': [4],
    'fisheye-kb3': [4],
    fisheye624: [12],
    'e57-pinhole': [0],
    'e57-spherical': [0],
    'e57-cylindrical': [0],
};
/**
 * Makes a coefficient list one the model actually accepts.
 *
 * The UI keeps distortion values across a change of camera model — switching
 * from OpenCV fisheye to ideal pinhole leaves four k's behind — and the kernel
 * refuses a list whose length does not match the model. That used to be
 * invisible: the projection failed and a JavaScript fallback silently produced
 * an undistorted result instead. With no fallback left it is an error, so the
 * list is fitted here, where the model is known, rather than passed on and
 * rejected: a model that takes no coefficients gets none, and a short or long
 * list is padded with zeros or truncated to the nearest accepted length.
 */
export function fitCoefficientsToModel(model, coefficients) {
    const accepted = ACCEPTED_COEFFICIENT_COUNTS[model] ?? [0];
    if (accepted.includes(coefficients.length)) {
        return [...coefficients];
    }
    const target = accepted.find(count => count >= coefficients.length) ?? accepted[accepted.length - 1];
    const fitted = coefficients.slice(0, target).map(value => (Number.isFinite(value) ? value : 0));
    while (fitted.length < target) {
        fitted.push(0);
    }
    return fitted;
}
export function cameraCoefficientsFromParameters(params) {
    return resolveCameraModel(params).coefficients;
}
/**
 * The undistorted model each distorted one collapses to when every coefficient
 * is zero. Exact, not an approximation: with zero coefficients the OpenCV
 * pinhole distortion is the identity, and the fisheye radial polynomial reduces
 * to `radius = theta`, which is the equidistant model by definition.
 */
const UNDISTORTED_EQUIVALENT = {
    'pinhole-opencv': 'pinhole-ideal',
    'fisheye-opencv': 'fisheye-equidistant',
    'fisheye-kb3': 'fisheye-equidistant',
    fisheye624: 'fisheye-equidistant',
};
/**
 * The model and coefficients the kernel should actually be given.
 *
 * Two reductions happen here, and both matter for speed as much as for
 * correctness:
 *
 * 1. A rectified image has had its distortion removed already, so it is an
 *    ideal pinhole whatever the file says.
 * 2. **All-zero coefficients mean no distortion**, so the distorted model is
 *    replaced by its undistorted equivalent. This is not a nicety: the
 *    distorted models have no closed-form unprojection, so every pixel runs a
 *    Newton solve, and a 5120x5120 depth image at `fisheye624` with twelve
 *    zeros spent about twenty seconds iterating — much of it on pixels outside
 *    the model domain, which only fail after exhausting their iterations — to
 *    produce exactly what the equidistant closed form gives immediately.
 */
export function resolveCameraModel(params) {
    if (params.imageRectified) {
        return { model: 'pinhole-ideal', coefficients: [] };
    }
    const declared = params.cameraModel;
    const coefficients = coefficientsForModel(declared, params);
    if (coefficients.every(value => value === 0)) {
        const undistorted = UNDISTORTED_EQUIVALENT[declared];
        if (undistorted) {
            return { model: undistorted, coefficients: [] };
        }
    }
    // Fisheye624 carrying only its first four radial terms *is* Kannala-Brandt:
    // the radial polynomial is the same one with two further terms, and the
    // tangential and thin-prism parts are what the extra six coefficients are
    // for. Saying so costs a third of the time, because the radial inversion is
    // a bisection over up to 512 steps and each one evaluates the polynomial —
    // six terms instead of four, per step, per pixel.
    if (declared === 'fisheye624' && coefficients.slice(4).every(value => value === 0)) {
        return { model: 'fisheye-kb3', coefficients: coefficients.slice(0, 4) };
    }
    return { model: declared, coefficients };
}
export function effectiveCameraModel(params) {
    return resolveCameraModel(params).model;
}
/**
 * The camera models the depth panel offers, and the coefficient count each one
 * shows. Every other model is a special case of one of these two: an ideal
 * pinhole is an OpenCV pinhole with zero distortion, and the equidistant,
 * OpenCV and Kannala-Brandt fisheyes are all the general fisheye with terms
 * left at zero (see `radial`, where OpenCV fisheye and KB3 share one code path
 * and Fisheye624 is that same polynomial with two further terms).
 *
 * The list is short because unused coefficients now cost nothing:
 * `resolveCameraModel` reduces a configuration to the cheapest model that is
 * exactly equivalent before it reaches the kernel.
 */
export const OFFERED_CAMERA_MODELS = {
    pinhole: 'pinhole-opencv',
    fisheye: 'fisheye624',
};
/**
 * Maps any camera model onto one the panel offers, moving the coefficients to
 * the slots the general model expects.
 *
 * Calibration files and saved settings still name the specific models -
 * COLMAP, ZED, RealSense and the YAML parsers all do - and a `<select>` with no
 * matching option silently falls back to its first entry, which would change
 * the model behind the user's back. Nothing is lost in the mapping: the values
 * land in the same polynomial positions they had.
 */
export function normalizeToOfferedCameraModel(model, coefficients = []) {
    const pad = (values, length) => {
        const out = values.slice(0, length).map(value => (Number.isFinite(value) ? value : 0));
        while (out.length < length) {
            out.push(0);
        }
        return out;
    };
    switch (model) {
        case 'pinhole-ideal':
            return { model: OFFERED_CAMERA_MODELS.pinhole, coefficients: pad([], 14) };
        case 'pinhole-opencv':
            return { model: OFFERED_CAMERA_MODELS.pinhole, coefficients: pad(coefficients, 14) };
        case 'fisheye-equidistant':
            return { model: OFFERED_CAMERA_MODELS.fisheye, coefficients: pad([], 12) };
        // The four radial terms occupy the same first four slots either way; only
        // the labels differ (OpenCV counts from k1, KB3 and the general model from
        // k0).
        case 'fisheye-opencv':
        case 'fisheye-kb3':
            return { model: OFFERED_CAMERA_MODELS.fisheye, coefficients: pad(coefficients, 12) };
        case 'fisheye624':
            return { model: OFFERED_CAMERA_MODELS.fisheye, coefficients: pad(coefficients, 12) };
        // The E57 panoramas belong to a file format rather than to this panel.
        default:
            return { model, coefficients: [...coefficients] };
    }
}
/** The coefficient list for a model, before the all-zero reduction. */
function coefficientsForModel(model, params) {
    if (params.coefficients) {
        return fitCoefficientsToModel(model, params.coefficients);
    }
    if (model === 'pinhole-opencv') {
        return [params.k1 ?? 0, params.k2 ?? 0, params.p1 ?? 0, params.p2 ?? 0, params.k3 ?? 0];
    }
    if (model === 'fisheye-opencv' || model === 'fisheye-kb3') {
        return [params.k1 ?? 0, params.k2 ?? 0, params.k3 ?? 0, params.k4 ?? 0];
    }
    return fitCoefficientsToModel(model, []);
}
export function validateCameraModelParameters(params) {
    const errors = [];
    const model = effectiveCameraModel(params);
    if (!Number.isFinite(params.fx) || params.fx <= 0) {
        errors.push('fx must be finite and positive');
    }
    const fy = params.fy ?? params.fx;
    if (!Number.isFinite(fy) || fy <= 0) {
        errors.push('fy must be finite and positive');
    }
    if (!Number.isFinite(params.cx) || !Number.isFinite(params.cy)) {
        errors.push('cx and cy must be finite');
    }
    const coefficients = params.imageRectified ? [] : (params.coefficients ?? []);
    const expected = CAMERA_MODEL_COEFFICIENTS[model].length;
    const validCoefficientCount = model === 'pinhole-opencv'
        ? [4, 5, 8, 12, 14].includes(coefficients.length)
        : coefficients.length === expected;
    if (!validCoefficientCount) {
        const expectedDescription = model === 'pinhole-opencv' ? '4, 5, 8, 12, or 14' : `${expected}`;
        errors.push(`${model} requires ${expectedDescription} coefficients (${CAMERA_MODEL_COEFFICIENTS[model].join(', ') || 'none'}), got ${coefficients.length}`);
    }
    if (coefficients.some(value => !Number.isFinite(value))) {
        errors.push('camera coefficients must be finite');
    }
    return errors;
}
export function projectCameraRay(params, ray) {
    const errors = validateCameraModelParameters(params);
    if (errors.length) {
        throw new Error(errors.join('; '));
    }
    const result = projectCameraRayWasmSync(ray, {
        ...params,
        cameraModel: effectiveCameraModel(params),
        coefficients: params.imageRectified ? [] : [...(params.coefficients ?? [])],
        fy: params.fy ?? params.fx,
        cx: params.cx,
        cy: params.cy,
    });
    if (!result) {
        throw new Error('The Rust/WASM camera-model kernel is not initialized');
    }
    return result;
}
export function unprojectCameraPixel(params, pixel) {
    const errors = validateCameraModelParameters(params);
    if (errors.length) {
        throw new Error(errors.join('; '));
    }
    const result = unprojectCameraPixelWasmSync(pixel, {
        ...params,
        cameraModel: effectiveCameraModel(params),
        coefficients: params.imageRectified ? [] : [...(params.coefficients ?? [])],
        fy: params.fy ?? params.fx,
        cx: params.cx,
        cy: params.cy,
    });
    if (!result) {
        throw new Error('The Rust/WASM camera-model kernel is not initialized');
    }
    return result;
}
export const CAMERA_MODEL_COEFFICIENT_GROUPS = {
    'pinhole-opencv': [
        {
            label: 'Radial',
            terms: ['k1', 'k2', 'k3', 'k4', 'k5', 'k6'],
            positions: [0, 1, 4, 5, 6, 7],
        },
        { label: 'Tangential', terms: ['p1', 'p2'], positions: [2, 3] },
        { label: 'Thin prism', terms: ['s1', 's2', 's3', 's4'], positions: [8, 9, 10, 11] },
        { label: 'Tilted sensor', terms: ['tauX', 'tauY'], positions: [12, 13] },
    ],
    fisheye624: [
        {
            label: 'Radial',
            terms: ['k0', 'k1', 'k2', 'k3', 'k4', 'k5'],
            positions: [0, 1, 2, 3, 4, 5],
        },
        { label: 'Tangential', terms: ['p0', 'p1'], positions: [6, 7] },
        { label: 'Thin prism', terms: ['s0', 's1', 's2', 's3'], positions: [8, 9, 10, 11] },
    ],
    'fisheye-opencv': [{ label: 'Radial', terms: ['k1', 'k2', 'k3', 'k4'], positions: [0, 1, 2, 3] }],
    'fisheye-kb3': [{ label: 'Radial', terms: ['k0', 'k1', 'k2', 'k3'], positions: [0, 1, 2, 3] }],
};
/** Total ordered length of a model's coefficient array, from its groups. */
function orderedLength(groups) {
    return groups.reduce((highest, group) => Math.max(highest, ...group.positions.map(position => position + 1)), 0);
}
/**
 * Assembles the ordered coefficient array from what each group's box holds.
 *
 * A box may hold fewer numbers than its group has terms, or be empty: missing
 * values are zero, which is what lets a calibration that gives two radial terms
 * be typed as two numbers.
 */
export function coefficientsFromGroups(model, groupValues) {
    const groups = CAMERA_MODEL_COEFFICIENT_GROUPS[model];
    if (!groups) {
        return [];
    }
    const ordered = new Array(orderedLength(groups)).fill(0);
    groups.forEach((group, groupIndex) => {
        const entered = (groupValues[groupIndex] ?? '')
            .split(',')
            .map(value => value.trim())
            .filter(value => value !== '');
        group.positions.forEach((position, termIndex) => {
            const value = Number(entered[termIndex]);
            ordered[position] = Number.isFinite(value) ? value : 0;
        });
    });
    return ordered;
}
/** The inverse: what each group's box should show for an ordered array. */
export function groupsFromCoefficients(model, coefficients) {
    const groups = CAMERA_MODEL_COEFFICIENT_GROUPS[model];
    if (!groups) {
        return [];
    }
    return groups.map(group => {
        const values = group.positions.map(position => coefficients[position] ?? 0);
        // Trailing zeros are dropped: a box showing "-0.05" says as much as one
        // showing "-0.05,0,0,0,0,0" and is far easier to read and edit.
        while (values.length > 0 && values[values.length - 1] === 0) {
            values.pop();
        }
        return values.join(',');
    });
}
